import { AttachFile } from "modules/UI/components/AttachFile.component";
import { Attachment } from "modules/UI/components/Attachment.component";
import AddIcon from "@mui/icons-material/Add";
import { 
  Box, 
  List,  
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  Typography, } from "@mui/material";
  import {
  Visibility,
  Delete,
  PictureAsPdf,
} from "@mui/icons-material";
import moment from "moment";
import { useState } from "react";
import React from "react";
import { Accept, FileRejection, useDropzone } from "react-dropzone";
import { useConfirmation } from "../hooks/useConfirmation.hook";
import {
  AuthenticatedImageBackgroundDiv,
} from "modules/UI/components/AuthenticatedImage.component";

type Attachment = {
  creationDate: string;
  fileName: string;
  id: string | number;
  uploadedByUserEmail: string;
};

export const Attachments = (props: {
  attachments: Attachment[];
  enableDelete?: boolean;
  enableUpload?: boolean;
  isDeleting?: boolean;
  isLoading?: boolean;
  isUploading?: boolean;
  accept?: Accept;
  readOnly?: boolean;
  multiple?: boolean;
  fullHeight?: boolean;
  minSize?: number;
  maxSize?: number;
  maxFiles?: number;
  placeholder?: string | React.Component | JSX.Element;
  onAttach?: (files: any) => void;
  onClick?: (files: any) => void;
  onDelete?: (attachment: Attachment) => void;
}) => {
  const {
    attachments,
    enableDelete,
    enableUpload,
    isDeleting,
    isLoading,
    isUploading,
    accept,
    multiple,
    minSize,
    maxSize,
    maxFiles,
    onAttach,
    onClick,
    onDelete,
  } = props;
  const deleteConfirmation = useConfirmation({
    title: "Delete attachment",
    description: "This action will delete this file forever.",
    submitButtonText: "Yes, delete it.",
    cancelButtonText: "No, keep it.",
  });
  const [deleteAttachment, setDeleteAttachment] = useState(null);
  const [fileRejections, setFileRejections] = useState<FileRejection[]>(null);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    noClick: true,
    onDrop: (acceptedFiles, fileRejections, dropEvent) => {
      setFileRejections(fileRejections);
      onAttach(acceptedFiles);
    },
    accept,
    multiple,
    maxSize,
    minSize: minSize || 1,
    maxFiles,
  });

  return isLoading ? (
    <>loading attachments</>
  ) : (
    <Box
      {...getRootProps()}
      sx={{
        transition: ".2s ease-out",
        margin: props.fullHeight ? "0" : "1rem 0",
        height: props.fullHeight ? "100%" : "auto",
        border: isDragActive
          ? "2px dashed currentColor"
          : "2px solid transparent",
      }}
    >
      {deleteConfirmation.JSX}
      <List>
        {/* <NSPConfirmation
        description="This action will delete this file forever."
        confirmLabel="Yes, delete it."
        cancelLabel="No, keep it."
        isLoadingConfirm={isDeleting}
        visible={!!deleteAttachment}
        onCancel={() => {
          setDeleteAttachment(null);
        }}
        onConfirm={() => {
          onDelete(deleteAttachment);
          setDeleteAttachment(null);
        }}
      /> */}
        {/* {attachments?.map((attachment) => (
          <Attachment
            key={attachment.id}
            fileName={attachment.fileName}
            secondary={`uploaded ${moment
              .utc(attachment.creationDate)
              .fromNow()} by ${attachment.uploadedByUserEmail}`}
            onDelete={
              !enableDelete || isDeleting
                ? null
                : () =>
                    deleteConfirmation
                      .requestConfirmation()
                      .then(() => {
                        onDelete(attachment);
                        setDeleteAttachment(null);
                      })
                      .catch((e) => {
                        setDeleteAttachment(null);
                      })
            }
            onClick={() => onClick(attachment)}
          />
        ))} */}
        {attachments?.length > 0 && (
  <ImageList cols={4} gap={16} sx={{ mb: 2 }}>
    {attachments.map((attachment) => {
      const fileName = attachment.fileName || "";
      const lowerFileName = fileName.toLowerCase();

      const isImage =
        lowerFileName.endsWith(".png") ||
        lowerFileName.endsWith(".jpg") ||
        lowerFileName.endsWith(".jpeg") ||
        lowerFileName.endsWith(".gif") ||
        lowerFileName.endsWith(".webp");

      const isPdf = lowerFileName.endsWith(".pdf");

      // Build preview URL based on existing onClick behavior
      // (If your attachment object already has `url`, use that instead.)
      const previewSrc = (attachment as any).url;

      return (
        <ImageListItem
          key={attachment.id}
          sx={{
            border: "1px solid #ddd",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          {/* Preview Area */}
          {isImage && previewSrc ? (
            <AuthenticatedImageBackgroundDiv
              src={previewSrc}
              style={{
                width: "100%",
                height: 180,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          ) : isPdf ? (
            <Box
              sx={{
                height: 180,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                bgcolor: "#fafafa",
              }}
            >
              <PictureAsPdf
                sx={{ fontSize: 64, color: "error.main" }}
              />
              <Typography variant="caption">
                PDF Document
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                height: 180,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#fafafa",
                p: 2,
              }}
            >
              <Typography
                variant="caption"
                textAlign="center"
                sx={{ wordBreak: "break-word" }}
              >
                {fileName}
              </Typography>
            </Box>
          )}

          {/* Footer */}
          <ImageListItemBar
            title={fileName}
            subtitle={`uploaded ${moment
              .utc(attachment.creationDate)
              .fromNow()}`}
            actionIcon={
              <>
                <IconButton
                  sx={{ color: "white" }}
                  onClick={() => onClick?.(attachment)}
                >
                  <Visibility />
                </IconButton>

                {enableDelete && !isDeleting && (
                  <IconButton
                    sx={{ color: "white" }}
                    onClick={() =>
                      deleteConfirmation
                        .requestConfirmation()
                        .then(() => {
                          onDelete?.(attachment);
                          setDeleteAttachment(null);
                        })
                        .catch(() => {
                          setDeleteAttachment(null);
                        })
                    }
                  >
                    <Delete />
                  </IconButton>
                )}
              </>
            }
          />
        </ImageListItem>
      );
    })}
  </ImageList>
)}
        {enableUpload && (
          <AttachFile
            isLoading={isUploading}
            // icon={<AddIcon />}
            onAttach={onAttach}
            //inputProps={{ ...getInputProps() }}
            inputProps={getInputProps()}
          />
        )}
      </List>
    </Box>
  );
};
